import { z } from 'zod';
import { localeSchema, timezoneSchema } from './common.js';
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
/**
 * Minimum 12 characters, with no composition rules. Length beats character
 * classes for real-world strength, and this is a private homelab instance.
 */
export const passwordSchema = z.string().min(12, 'must be at least 12 characters').max(200);
export const displayNameSchema = z.string().trim().min(1).max(80);
export const registerSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    displayName: displayNameSchema,
});
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(200),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
export const authTokensSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Seconds until `accessToken` expires. */
    expiresIn: z.number().int().positive(),
});
/** Daily targets. Any target left null simply is not shown in the UI. */
export const goalsSchema = z.object({
    kcal: z.number().min(0).max(20000).nullable().default(null),
    protein: z.number().min(0).max(1000).nullable().default(null),
    carbs: z.number().min(0).max(2000).nullable().default(null),
    fat: z.number().min(0).max(1000).nullable().default(null),
    /**
     * Daily fibre target in grams. Unset falls back to the DGE recommendation of
     * 30 g in the UI, so the bar is useful before anything is configured.
     */
    fiber: z.number().min(0).max(200).nullable().default(null),
    weightKg: z.number().min(20).max(500).nullable().default(null),
    /** Daily water target in millilitres. */
    waterMl: z.number().min(0).max(20000).nullable().default(null),
});
export const preferencesSchema = z.object({
    locale: localeSchema.default('de'),
    timezone: timezoneSchema.default('Europe/Berlin'),
    /**
     * Show the body-fat field alongside weight. Off by default: it only makes
     * sense with a scale that measures it, and an always-present empty field
     * would be noise for everyone else. Turning it off hides the input but never
     * deletes readings already recorded.
     */
    trackBodyFat: z.boolean().default(false),
    /**
     * Derive maintenance calories from what was actually eaten and how the weight
     * trend moved. Off by default because it is worthless until several weeks of
     * near-complete logging exist, and a number that arrives before then would be
     * trusted more than it deserves.
     */
    adaptiveTdee: z.boolean().default(false),
    /**
     * Hold writes made while offline and send them on reconnect. Off by default:
     * it changes what "saved" means — an entry can be recorded on the phone and
     * not yet on the server — and that is a trade the user should make knowingly.
     */
    offlineQueue: z.boolean().default(false),
});
export const publicUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string(),
    role: z.enum(['admin', 'user']),
    goals: goalsSchema,
    preferences: preferencesSchema,
    createdAt: z.string(),
});
export const updateProfileSchema = z
    .object({
    displayName: displayNameSchema.optional(),
    goals: goalsSchema.partial().optional(),
    preferences: preferencesSchema.partial().optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'no fields to update');
export const authResponseSchema = z.object({
    user: publicUserSchema,
    tokens: authTokensSchema,
});
//# sourceMappingURL=auth.js.map